import { useState, useEffect, useCallback } from "react";
import { X, Plus, Pencil, Trash2, Save, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";

/**
 * Modal CRUD de mensagens pré-cadastradas para a "Observação do Cadastro".
 * Ao clicar em uma mensagem da lista, chama `onPick(texto)` — o consumidor
 * (VisitanteObsSaidaModal) decide como inserir o texto no textarea.
 */
export default function MsgObsAcessoManagerModal({ open, onOpenChange, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formTexto, setFormTexto] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await clienteApi.get("/cliente/msg-obs-acesso");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error("Falha ao carregar mensagens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchAll();
      setEditingId(null);
      setFormTexto("");
    }
  }, [open, fetchAll]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const handleNovo = () => {
    setEditingId(null);
    setFormTexto("");
  };

  const handleEditar = (row) => {
    setEditingId(row.id);
    setFormTexto(row.texto || "");
  };

  const handleSalvar = async () => {
    const texto = (formTexto || "").trim();
    if (!texto) { toast.error("Informe o texto da mensagem"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await clienteApi.put(`/cliente/msg-obs-acesso/${editingId}`, { texto });
        toast.success("Mensagem atualizada");
      } else {
        await clienteApi.post("/cliente/msg-obs-acesso", { texto });
        toast.success("Mensagem incluída");
      }
      setEditingId(null);
      setFormTexto("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async (row) => {
    if (!window.confirm(`Excluir a mensagem "${(row.texto || "").slice(0, 60)}..."?`)) return;
    try {
      await clienteApi.delete(`/cliente/msg-obs-acesso/${row.id}`);
      toast.success("Mensagem excluída");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const handlePick = (row) => {
    if (onPick) onPick(row.texto || "");
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="msg-obs-manager-modal"
    >
      <div className="bg-[#0f1a30] border border-brand-ice/20 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-brand-ice/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-brand-medium/30 text-brand-light">
              <MessageSquareText className="w-5 h-5" />
            </div>
            <h3 className="font-heading font-bold text-brand-ice text-base">
              Mensagens pré-cadastradas
            </h3>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
            title="Fechar (Esc)"
            data-testid="msg-obs-manager-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {/* Form novo/editar */}
          <div className="bg-white/5 border border-brand-ice/10 rounded-lg p-3 space-y-2">
            <label className="text-[10px] uppercase tracking-wider font-heading font-bold text-brand-ice/70">
              {editingId ? "Editar mensagem" : "Nova mensagem"}
            </label>
            <textarea
              value={formTexto}
              onChange={(e) => setFormTexto(e.target.value)}
              placeholder="Ex.: Saída após 22h — visitante autorizado pelo morador"
              rows={2}
              className="w-full rounded-md bg-white/10 border border-brand-ice/20 text-brand-ice placeholder:text-brand-ice/40 focus:outline-none focus:ring-2 focus:ring-brand-light/40 focus:border-brand-light p-2 text-sm"
              data-testid="msg-obs-form-texto"
            />
            <div className="flex justify-end gap-2">
              {editingId && (
                <button
                  type="button"
                  onClick={handleNovo}
                  className="px-3 py-1.5 rounded-md text-xs text-brand-ice/70 hover:text-brand-ice hover:bg-white/10 transition-colors"
                >
                  Cancelar
                </button>
              )}
              <button
                type="button"
                onClick={handleSalvar}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-medium hover:bg-brand-dark text-white text-xs font-heading font-bold transition-colors disabled:opacity-60"
                data-testid="msg-obs-form-salvar"
              >
                {editingId ? <Save className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {saving ? "Salvando..." : (editingId ? "Salvar" : "Incluir")}
              </button>
            </div>
          </div>

          {/* Lista */}
          <div className="text-[10px] uppercase tracking-wider font-heading font-bold text-brand-ice/70 pl-1">
            {items.length === 0 ? "Nenhuma mensagem cadastrada" : `Clique numa mensagem para colar no textarea (${items.length})`}
          </div>
          {loading ? (
            <div className="text-center py-6 text-brand-ice/50 text-sm">Carregando...</div>
          ) : (
            <ul className="divide-y divide-brand-ice/10 rounded-lg overflow-hidden border border-brand-ice/10">
              {items.map((row) => (
                <li
                  key={row.id}
                  data-testid={`msg-obs-row-${row.id}`}
                  className="flex items-center gap-2 p-2.5 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => handlePick(row)}
                    className="flex-1 text-left text-sm text-brand-ice hover:text-brand-light transition-colors whitespace-pre-wrap break-words"
                    title="Clique para colar no campo Observação do Cadastro"
                    data-testid={`msg-obs-pick-${row.id}`}
                  >
                    {row.texto}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEditar(row)}
                    className="p-1.5 rounded-md text-brand-light hover:text-white hover:bg-brand-light/10 transition-colors"
                    title="Editar"
                    data-testid={`msg-obs-editar-${row.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExcluir(row)}
                    className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-500/10 transition-colors"
                    title="Excluir"
                    data-testid={`msg-obs-excluir-${row.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
